import { useState, useRef, useEffect, useCallback } from 'react';
import { ZoomIn, X, Maximize2, Minimize2 } from 'lucide-react';

/**
 * Composant GeoGebra interactif read-only pour l'élève.
 * Charge l'applet complet depuis ggb_base64 (zoom/pan activés, objets fixes).
 * Fallback sur l'image statique ggb_image si ggb_base64 absent ou CDN indisponible.
 * Bouton "Agrandir" → modal plein écran avec l'image PNG.
 */
const GeogebraElementEleve = ({ config, allowExpand = true }) => {
  const [loading, setLoading] = useState(true);
  const [cdnFailed, setCdnFailed] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const ggbApiRef = useRef(null);
  const ggbAppletRef = useRef(null);
  const ggbElementId = useRef(`ggb-eleve-${Date.now()}`);
  const initializedRef = useRef(false);
  const intervalIdsRef = useRef([]);
  // Capturer config au mount pour l'utiliser dans le useEffect sans dépendance
  const configRef = useRef(config);

  // Charger le script GeoGebra depuis le CDN
  const loadGeoGebra = useCallback(async () => {
    if (typeof window.GGBApplet !== 'undefined') return true;

    const urls = [
      'https://www.geogebra.org/apps/deployggb.js',
      'https://cdn.geogebra.org/apps/deployggb.js'
    ];

    for (const cdnUrl of urls) {
      try {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = cdnUrl;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
        if (typeof window.GGBApplet !== 'undefined') return true;
      } catch (e) {
        console.warn('GeoGebra CDN failed:', cdnUrl);
      }
    }
    return false;
  }, []);

  // Nettoyage complet de l'applet GeoGebra
  const cleanup = useCallback(() => {
    if (ggbApiRef.current) {
      try {
        ggbApiRef.current.unregisterAddListener();
        ggbApiRef.current.unregisterUpdateListener();
        ggbApiRef.current.unregisterRemoveListener();
      } catch (e) {}
      ggbApiRef.current = null;
    }

    if (ggbAppletRef.current) {
      try {
        if (typeof ggbAppletRef.current.remove === 'function') {
          ggbAppletRef.current.remove();
        }
      } catch (e) {}
      ggbAppletRef.current = null;
    }

    const containerId = ggbElementId.current;
    const container = document.getElementById(containerId);
    if (container) {
      const iframe = container.querySelector('iframe');
      if (iframe) {
        iframe.src = 'about:blank';
        iframe.remove();
      }
      container.innerHTML = '';
    }

    if (window[containerId]) {
      try {
        delete window[containerId];
      } catch (e) {
        window[containerId] = undefined;
      }
    }

    if (intervalIdsRef.current.length > 0) {
      intervalIdsRef.current.forEach(id => clearInterval(id));
      intervalIdsRef.current = [];
    }

    initializedRef.current = false;
  }, []);

  // Initialiser l'applet GeoGebra en mode lecture seule
  useEffect(() => {
    const cfg = configRef.current;
    if (!cfg?.ggb_base64) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    const init = async () => {
      const loaded = await loadGeoGebra();
      if (!loaded) {
        setCdnFailed(true);
        setLoading(false);
        return;
      }

      const ggbParams = {
        appName: 'classic',
        perspective: 'G',
        width: 550,
        height: 400,
        showToolBar: false,
        customToolBar: '',
        showAlgebraInput: false,
        showAlgebraView: false,
        algebraWidth: 0,
        showMenuBar: false,
        showResetIcon: false,
        showToolBarHelp: false,
        enableLabelDrags: false,
        enableShiftDragZoom: true,
        enableRightClick: false,
        enableUndoRedo: false,
        enableCAS: false,
        disableAutoScale: true,
        showZoomButtons: true,
        showFullscreenButton: false,
        showSuggestionButtons: false,
        showAuxiliaryObjects: false,
        allowStyleBar: false,
        showAnimationButton: false,
        showStartTooltip: false,
        showLogging: false,
        useBrowserForJS: true,
        borderColor: '#ffffff',
        showGrid: true,
        showAxes: true,
        language: 'fr',
        appletOnLoad: (api) => {
          ggbApiRef.current = api;
          setLoading(false);

          // Restaurer la figure du prof
          api.setBase64(cfg.ggb_base64);

          // setBase64 est async : fixer les objets après chargement
          setTimeout(() => {
            try {
              const names = api.getAllObjectNames();
              for (let i = 0; i < names.length; i++) {
                api.setFixed(names[i], true);
              }
            } catch (e) {}
          }, 500);

          // Appliquer grille/axes selon la config du prof
          try {
            const showGrid = cfg.gridVisible ?? true;
            const showAxes = cfg.axesVisible ?? true;
            api.setGridVisible(showGrid);
            api.setAxesVisible(showAxes, showAxes);

            // Afficher les labels des points
            const objNames = api.getAllObjectNames();
            for (let i = 0; i < objNames.length; i++) {
              const name = objNames[i];
              if (api.getObjectType(name) === 'point') {
                api.setLabelVisible(name, true);
              }
            }

            // Masquer l'algèbre + fixer les objets
            api.setPerspective('G');
            api.setVisible('algebra', false);
            for (let i = 0; i < objNames.length; i++) {
              api.setAlgebraVisible(objNames[i], false);
              api.setFixed(objNames[i], true);
            }
          } catch (e) {}

          // Forcer un redraw après initialisation
          setTimeout(() => {
            try {
              api.evalCommand('UpdateConstruction()');
            } catch (e) {}
          }, 300);
        }
      };

      const applet = new window.GGBApplet(ggbParams, true);
      ggbAppletRef.current = applet;

      // Tracker les intervals créés par GeoGebra pour le cleanup
      const originalSetInterval = window.setInterval;
      window.setInterval = function (...args) {
        const id = originalSetInterval.apply(window, args);
        intervalIdsRef.current.push(id);
        return id;
      };

      applet.inject(ggbElementId.current);

      setTimeout(() => {
        window.setInterval = originalSetInterval;
      }, 3000);
    };

    init();
    return cleanup;
  }, [loadGeoGebra, cleanup]);

  // Toggle taille via CSS scale (évite le re-layout interne de GeoGebra)
  const SCALE = 1.6;
  const toggleExpand = () => setIsExpanded(prev => !prev);

  // Rien à afficher
  if (!config?.ggb_base64 && !config?.ggb_image) return null;

  const imgSrc = config?.ggb_image ? `data:image/png;base64,${config.ggb_image}` : null;

  // Modal zoom image (pour le fallback image statique)
  const zoomModal = isZoomed && imgSrc ? (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4"
      onClick={() => setIsZoomed(false)}
    >
      <button
        onClick={() => setIsZoomed(false)}
        className="absolute top-4 right-4 w-10 h-10 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center text-white transition-colors z-10"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="absolute top-4 left-4 px-3 py-1.5 bg-slate-800/80 backdrop-blur-sm rounded-full text-slate-400 text-xs">
        Cliquez n'importe où pour fermer
      </div>
      <div
        className="bg-white rounded-2xl p-2 max-w-[95vw] max-h-[90vh] overflow-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <img src={imgSrc} alt="Figure géométrique" className="w-full h-fit object-contain" />
      </div>
    </div>
  ) : null;

  // Fallback image statique (CDN échoué ou pas de ggb_base64)
  if (cdnFailed || !config?.ggb_base64) {
    if (!imgSrc) return null;

    return (
      <>
        <div
          className="group relative bg-white rounded-xl overflow-hidden my-3 border border-slate-600 cursor-pointer hover:border-slate-400 transition-all"
          onClick={() => setIsZoomed(true)}
        >
          <img src={imgSrc} alt="Figure géométrique" className="w-full h-auto max-w-lg mx-auto" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-end justify-center pb-3 opacity-0 group-hover:opacity-100">
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-500/80 backdrop-blur-sm rounded-full text-white text-xs font-medium">
              <ZoomIn className="w-3.5 h-3.5" />
              Agrandir
            </div>
          </div>
        </div>
        {zoomModal}
      </>
    );
  }

  // Mode interactif : applet GeoGebra read-only
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-slate-600 my-3 mx-auto w-fit transition-all">
      <style>{`
        #${ggbElementId.current} .ggb_logo,
        #${ggbElementId.current} .GeoGebraLogo,
        #${ggbElementId.current} [class*="logo"],
        #${ggbElementId.current} .examButton {
          display: none !important;
        }
      `}</style>

      <div className="relative overflow-hidden" style={{ height: isExpanded ? 400 * SCALE : 400, width: isExpanded ? 550 * SCALE : undefined }}>
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 z-10">
            <div className="w-8 h-8 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mb-2" />
            <span className="text-sm text-slate-500">Chargement de la figure...</span>
          </div>
        )}
        <div
          id={ggbElementId.current}
          style={isExpanded ? { transform: `scale(${SCALE})`, transformOrigin: 'top left' } : undefined}
        />
      </div>

      {/* Barre d'actions */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-t border-slate-200">
        <p className="text-slate-400 text-xs">
          Molette ou +/- pour zoomer, glissez pour déplacer
        </p>
        {allowExpand && (
          <button
            onClick={toggleExpand}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-600 text-xs font-medium transition-colors"
          >
            {isExpanded ? (
              <><Minimize2 className="w-3.5 h-3.5" /> Réduire</>
            ) : (
              <><Maximize2 className="w-3.5 h-3.5" /> Agrandir</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default GeogebraElementEleve;
